import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Input,
} from "reactstrap";

import weatherImage from "../../assets/images/cloud.png";
import temperatureIcon from "../../assets/images/temperature.svg";
import humidityIcon from "../../assets/images/humidity.svg";
import speedIcon from "../../assets/images/wind-speed.svg";
import rainfallIcon from "../../assets/images/umbrella.svg";
import lightIcon from "../../assets/images/eye.svg";
import locationIcon from "../../assets/images/location.svg";

import { withTranslation } from "react-i18next";
import useWeatherDataQuery from "store/WeatherData/useWeatherDataQuery";

const Dashboard = (props) => {
  const [date, setDate] = useState(new Date());

  const today = new Date();
  const dd = String(today.getDate()).padStart(2, "0");
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const yyyy = today.getFullYear();
  const currentDateInMMDDYYYY = `${yyyy}-${mm}-${dd}`;

  const [deviceId, setDeviceId] = useState("DFWSA1012");
  const [filterDate, setFilterDate] = useState(currentDateInMMDDYYYY);

  const [dashboardData, setDashboardData] = useState({
    temperature: 0,
    humidity: 0,
    direction: 0,
    speed: 0,
    rainfall: 0,
    pressure: 0,
    time: new Date(),
    lux: 0,
    id: 0,
  });

  const [dashboardRainData, setDashboardRainData] = useState({
    rainFall: 0,
  });

  // Base query
  const baseQuery = {
    deviceId,
    limit: 10,
    offset: 0,
  };

  // Rain deviceId logic
  const rainDeviceId = deviceId === "DFWSA1012" ? "DFWSA1016" : deviceId;

  // All queries
  const { data: weatherList, isSuccess, refetch: weatherDataRefetch } =
    useWeatherDataQuery.weatherData(baseQuery);

  const { data: rainList, isSuccess: rainDataSuccess, refetch: rainDataRefetch } =
    useWeatherDataQuery.rainData({
      ...baseQuery,
      deviceId: rainDeviceId,
    });

  const dailyQuery = {
    ...baseQuery,
    date: filterDate,
  };

  const { data: currentWeather, refetch: currentWeatherDataRefetch } =
    useWeatherDataQuery.currentWeatherData(dailyQuery);

  const { data: currentRain, refetch: currentRainDataRefetch } =
    useWeatherDataQuery.currentRainData({
      ...dailyQuery,
      deviceId: rainDeviceId,
    });

  // Update dashboard data
  useEffect(() => {
    if (isSuccess && weatherList?.results?.[0]) {
      setDashboardData(weatherList.results[0]);
    }
  }, [isSuccess, weatherList]);

  // Update rain data
  useEffect(() => {
    if (rainDataSuccess && rainList?.results?.[0]) {
      setDashboardRainData(rainList.results[0]);
    }
  }, [rainDataSuccess, rainList]);

  // Refetch every hour
  useEffect(() => {
    const interval = setInterval(() => {
      weatherDataRefetch();
      rainDataRefetch();
      currentWeatherDataRefetch();
      currentRainDataRefetch();
    }, 3600000);
    return () => clearInterval(interval);
  }, [
    deviceId,
    filterDate,
    weatherDataRefetch,
    rainDataRefetch,
    currentWeatherDataRefetch,
    currentRainDataRefetch,
  ]);

  document.title = "Dashboard | AVG AWS";

  const dateTime = date.toLocaleString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
  const currentDate = date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const roundOff = (num) => (!num ? 0 : num.toFixed(2));
  const convertToKph = (speed) => (!speed && speed === 0 ? 0 : speed * 3.6);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Header */}
          <div className="page-title-box">
            <Row className="align-items-center justify-content-between">
              <Col md={9} sm={12}>
                <h6 className="page-title">Dashboard</h6>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item active">
                    Welcome to AVG AWS Dashboard
                  </li>
                </ol>
              </Col>

              <Col md={3} sm={12}>
                <div className="d-flex justify-content-end flex-column">
                  <div className="text-end">
                    <h5>{currentDate}</h5>
                    <h6>{dateTime}</h6>
                  </div>
                  <div className="d-flex gap-2 justify-content-end">
                    <FormGroup>
                      <Label for="deviceId">Filter by Device Id</Label>
                      <Input
                        value={deviceId}
                        onChange={(e) => setDeviceId(e.target.value)}
                        type="select"
                        name="deviceId"
                        id="deviceId"
                      >
                        <option value={"DFWSA1012"}>Weather 1</option>
                        <option value={"DFWSA1013"}>Weather 2</option>
                      </Input>
                    </FormGroup>
                    <FormGroup>
                      <Label for="filterDate">Date</Label>
                      <Input
                        value={filterDate}
                        onChange={(e) => setFilterDate(e.target.value)}
                        type="date"
                        name="filterDate"
                        id="filterDate"
                      />
                    </FormGroup>
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          {/* Summary Cards */}
          <Row>
            <Col xl={12}>
              <Card style={{ borderRadius: "24px" }}>
                <CardBody>
                  <Row>
                    {deviceId !== "DFWSA1013" && (
                      <>
                        <Col md={3}>
                          <Card style={{ background: "#E3FDF2", borderRadius: "24px", paddingBottom: "10px" }}>
                            <CardBody>
                              <h5 className="text-black mb-3">Temperature</h5>
                              <h6 className="text-black">
                                High:{" "}
                                <span className="text-success">
                                  {roundOff(currentWeather?.data?.temperature?.high || 0)} (°C)
                                </span>
                              </h6>
                              <h6 className="text-black">
                                Low:{" "}
                                <span className="text-danger">
                                  {roundOff(currentWeather?.data?.temperature?.low || 0)} (°C)
                                </span>
                              </h6>
                            </CardBody>
                          </Card>
                        </Col>

                        <Col md={3}>
                          <Card style={{ background: "#E3FDF2", borderRadius: "24px", paddingBottom: "10px" }}>
                            <CardBody>
                              <h5 className="text-black mb-3">Wind Speed</h5>
                              <h6 className="text-black">
                                High:{" "}
                                <span className="text-success">
                                  {roundOff(convertToKph(currentWeather?.data?.speed?.high) || 0)} (kph)
                                </span>
                              </h6>
                              <h6 className="text-black">
                                Low:{" "}
                                <span className="text-danger">
                                  {roundOff(convertToKph(currentWeather?.data?.speed?.low) || 0)} (kph)
                                </span>
                              </h6>
                            </CardBody>
                          </Card>
                        </Col>
                      </>
                    )}

                    {/* Rainfall Cards */}
                    <Col md={3}>
                      <Card style={{ background: "#E3FDF2", borderRadius: "24px", paddingBottom: "10px" }}>
                        <CardBody>
                          <h5 className="text-black mb-3">Rain Fall</h5>
                          <h6 className="text-black">
                            High:{" "}
                            <span className="text-success">
                              {roundOff(currentRain?.data?.rainfall?.high || 0)} (mm)
                            </span>
                          </h6>
                          <h6 className="text-black">
                            Low:{" "}
                            <span className="text-danger">
                              {roundOff(currentRain?.data?.rainfall?.low || 0)} (mm)
                            </span>
                          </h6>
                        </CardBody>
                      </Card>
                    </Col>

                    <Col md={3}>
                      <Card style={{ background: "#E3FDF2", borderRadius: "24px", paddingBottom: "35px" }}>
                        <CardBody>
                          <h5 className="text-black mb-3">Consolidated Rain</h5>
                          <h6 className="text-black">
                            <span className="text-success">
                              {roundOff(currentRain?.data?.rainfall?.accumulated_rainfall || 0)} (mm)
                            </span>
                          </h6>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Weather Summary */}
          <Row>
            <Col xl={12}>
              <Card style={{ borderRadius: "24px" }}>
                <CardBody className="ps-0 pb-0">
                  <div
                    className="d-flex justify-content-start align-items-center gap-2 p-2 px-3 ms-3"
                    style={{
                      width: "130px",
                      backgroundColor: "#E3FDF2",
                      borderRadius: "24px",
                    }}
                  >
                    <img src={locationIcon} alt="location" />
                    <h6 className="mt-1">Kozhikode</h6>
                  </div>
                  <div className="ms-4">
                    <h3 className="card-title mt-2">Weather</h3>
                    <p className="text-muted mb-4">Now</p>
                  </div>
                  <Row>
                    <Col lg={6} style={{ position: "relative" }}>
                      <div style={{ position: "absolute", bottom: 0, left: 0 }}>
                        <img src={weatherImage} alt="weather" />
                      </div>
                    </Col>
                    <Col lg={6} className="mb-5">
                      <Row>
                        <Col md={4}>
                          <div className="text-start">
                            <img width={24} height={24} src={temperatureIcon} alt="temperature" />
                            <h5 className="mt-2">Temperature</h5>
                            <p className="text-muted mb-4">
                              {roundOff(dashboardData?.temperature || 0)} (°C)
                            </p>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="text-start">
                            <img width={24} height={24} src={humidityIcon} alt="humidity" />
                            <h5 className="mt-2">Humidity</h5>
                            <p className="text-muted mb-4">
                              {roundOff(dashboardData?.humidity || 0)} (%)
                            </p>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="text-start">
                            <img width={24} height={24} src={speedIcon} alt="speed" />
                            <h5 className="mt-2">Wind Speed</h5>
                            <p className="text-muted mb-4">
                              {roundOff(convertToKph(dashboardData?.speed) || 0)} (kph)
                            </p>
                          </div>
                        </Col>
                      </Row>
                      <hr />
                      <Row>
                        <Col md={4}>
                          <div className="text-start">
                            <img width={24} height={24} src={rainfallIcon} alt="rainfall" />
                            <h5 className="mt-2">Rain Fall</h5>
                            <p className="text-muted mb-4">
                              {dashboardRainData?.rainfall || 0}
                            </p>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="text-start">
                            <img width={24} height={24} src={lightIcon} alt="pressure" />
                            <h5 className="mt-2">Pressure</h5>
                            <p className="text-muted mb-4">
                              {roundOff(dashboardData?.pressure || 0)} (hPa)
                            </p>
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

Dashboard.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(Dashboard);
